import type { MarketingPreferencesData } from '../../domain/types'
import { mqMin, tok, tokPx } from '@sunwebgroup/ui-components'
import { Button, TextField, Typography } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { styled } from 'styled-components'
import marketingPreferencesUseCases from '../../domain/useCases'
import { ControlledCheckboxField } from './ControlledCheckboxField'

const Form = styled.form`
  background-color: ${tok('semantics_views_background_color')};
  padding: ${tokPx('primitives_space_24')};
  max-width: ${tokPx('primitives_area_size_800')};
  margin-bottom: ${tokPx('primitives_space_32')};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    margin-bottom: ${tokPx('primitives_space_64')};
    border-radius: ${tokPx('semantics_boxes_corner_radius')};
  }
`

const TypographyStyled = styled(Typography)`
  margin-top: ${tokPx('primitives_space_12')};
  margin-bottom: ${tokPx('primitives_space_20')};
`

const FormSection = styled.div`
  border-radius: ${tokPx('semantics_boxes_corner_radius')};
  background-color: ${tok('semantics_boxes_background_color')};
  border: ${tokPx('semantics_boxes_border_width')} solid ${tok('semantics_boxes_border_color')};
  padding: ${tokPx('semantics_boxes_padding')};
  margin-top: ${tokPx('primitives_space_12')};
`

const MaxWidthContainer = styled.div`
  max-width: ${tokPx('primitives_area_size_480')};
  width: 100%;
`

const FormFieldsContainer = styled(MaxWidthContainer)`
  display: flex;
  gap: ${tokPx('semantics_boxes_padding')};
  flex-direction: column;
  align-items: stretch;
`

const RemarkTypography = styled(Typography)`
  margin: ${tokPx('primitives_space_12')} 0;
`

const SubmitButton = styled(Button)`
  @media ${mqMin('primitives_breakpoint_m_min')} {
    width: auto;
  }
`

const ErrorMessage = styled(Typography)`
  color: ${tok('semantics_input_fields_invalid_icon_color')};
  margin-top: ${tokPx('primitives_space_2')};
`

export const MarketingPreferencesForm: React.FC = () => {
  const intl = useIntl()
  const [hasSubmitted, setHasSubmitted] = React.useState(false)
  const useCases = React.useMemo(() => marketingPreferencesUseCases(), [])

  const { control, handleSubmit, register, formState: { errors }, setError, clearErrors, getValues } = useForm<MarketingPreferencesData>({
    defaultValues: {
      emailAddress: '',
      unsubscribeChannels: {
        email: false,
        sms: false,
        post: false,
        mySunweb: false,
      },
      personalization: false,
      allBrands: false,
    },
  })

  const validateCheckboxGroup = () => {
    const values = getValues()
    const noneSelected = !values.unsubscribeChannels.email
      && !values.unsubscribeChannels.sms
      && !values.unsubscribeChannels.post
      && !values.unsubscribeChannels.mySunweb

    if (noneSelected) {
      setError('unsubscribeChannels', {
        type: 'manual',
        message: intl.formatMessage({ id: 'marketing_preferences_form_field_group_error' }),
      })
    }
    else {
      clearErrors('unsubscribeChannels')
    }
  }

  const handleCheckboxGroupValidation = () => {
    if (!hasSubmitted)
      return
    validateCheckboxGroup()
  }

  const onSubmit = (data: MarketingPreferencesData) => {
    useCases.updatePreferences(data)
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setHasSubmitted(true)
    validateCheckboxGroup()
    handleSubmit(onSubmit)(e)
  }

  return (
    <Form onSubmit={handleFormSubmit}>
      <Typography as="h2" $variant="display-g-regular">
        {intl.formatMessage({ id: 'marketing_preferences_form_title' })}
      </Typography>
      <MaxWidthContainer>
        <TypographyStyled as="p" $variant="body-m-bold-snug">
          {intl.formatMessage({ id: 'marketing_preferences_form_description_1' })}
        </TypographyStyled>
        <Typography as="p" $variant="body-m-bold-snug">
          {intl.formatMessage({ id: 'marketing_preferences_form_description_2' })}
        </Typography>
        <Typography as="p" $variant="body-m-regular-snug">
          {intl.formatMessage({ id: 'marketing_preferences_form_description_3' })}
        </Typography>
      </MaxWidthContainer>
      <FormSection>
        <FormFieldsContainer>
          <TextField
            label={intl.formatMessage({ id: 'marketing_preferences_form_field_label_email' })}
            {...register('emailAddress', {
              required: intl.formatMessage({ id: 'marketing_preferences_form_field_email_required' }),
              pattern: {
                value: /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/,
                message: intl.formatMessage({ id: 'marketing_preferences_form_field_email_invalid' }),
              },
            })}
            feedbackText={errors.emailAddress?.message}
            status={errors.emailAddress ? 'error' : undefined}
          />
          <Typography as="p" $variant="body-m-bold-snug">
            {intl.formatMessage({ id: 'marketing_preferences_form_field_label_unsubscribe_channels' })}
          </Typography>
          <ControlledCheckboxField
            name="unsubscribeChannels.email"
            control={control}
            label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_email' })}
            handleCheckboxGroupValidation={handleCheckboxGroupValidation}
          />
          <ControlledCheckboxField
            name="unsubscribeChannels.sms"
            control={control}
            label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_sms' })}
            handleCheckboxGroupValidation={handleCheckboxGroupValidation}
          />
          <ControlledCheckboxField
            name="unsubscribeChannels.post"
            control={control}
            label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_post' })}
            handleCheckboxGroupValidation={handleCheckboxGroupValidation}
          />
          <div>
            <ControlledCheckboxField
              name="unsubscribeChannels.mySunweb"
              control={control}
              label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_my_sunweb' })}
              handleCheckboxGroupValidation={handleCheckboxGroupValidation}
            />
            {errors.unsubscribeChannels && (
              <ErrorMessage as="p" $variant="body-s-regular-tight">
                {errors.unsubscribeChannels.message}
              </ErrorMessage>
            )}
          </div>

          <Typography as="p" $variant="body-m-bold-snug">
            {intl.formatMessage({ id: 'marketing_preferences_form_field_label_unsubscribe_others' })}
          </Typography>
          <ControlledCheckboxField
            name="personalization"
            control={control}
            label={intl.formatMessage({ id: 'marketing_preferences_form_field_checkbox_personalization' })}
          />
          <ControlledCheckboxField
            name="allBrands"
            control={control}
            label={
              intl.formatMessage(
                {
                  id: 'marketing_preferences_form_field_checkbox_all_brands',
                },
                {
                  privacyLink: (
                    <a href="/privacy" target="_blank" rel="noopener noreferrer">
                      {intl.formatMessage({ id: 'marketing_preferences_privacy_link' })}
                    </a>
                  ),
                },
              )
            }
          />
        </FormFieldsContainer>
      </FormSection>
      <MaxWidthContainer>
        <RemarkTypography as="p" $variant="body-s-regular-snug">
          {intl.formatMessage({ id: 'marketing_preferences_form_field_label_unsubscribe_remark' })}
        </RemarkTypography>
        <SubmitButton type="submit" $type="primary" $fullWidth>
          {intl.formatMessage({ id: 'marketing_preferences_form_submit_button' })}
        </SubmitButton>
      </MaxWidthContainer>
    </Form>
  )
}
